import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingCart, Tags, Settings, LogOut, Menu, X, Truck, Ticket, ClipboardList } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";

const nav = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/dispatch", label: "Daily Orders", icon: ClipboardList },
  { to: "/admin/shipping", label: "Shipping", icon: Truck },
  { to: "/admin/categories", label: "Categories", icon: Tags },
  { to: "/admin/offers", label: "Offers", icon: Ticket },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export const AdminLayout = ({ children, title }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const SidebarContent = () => (
    <>
      <div className="px-6 py-6 border-b border-cream/10 flex items-center gap-3">
        <img src={LOGO_URL} alt="Nel Bake House" className="h-12 w-12 rounded-full object-cover shrink-0" />
        <div>
          <span className="font-display text-lg font-bold text-cream block leading-none">NEL BAKE HOUSE</span>
          <p className="text-[10px] uppercase tracking-[0.2em] text-gold mt-1">Admin Panel</p>
        </div>
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {nav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`admin-nav-${n.label.toLowerCase()}`}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-4 py-3.5 text-sm font-medium transition-colors min-h-[48px] ${
                isActive ? "bg-gold text-chocodark" : "text-cream/80 hover:bg-cream/10"
              }`
            }
          >
            <n.icon size={20} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-cream/10">
        <button data-testid="admin-logout" onClick={doLogout} className="flex items-center gap-3 rounded-xl px-4 py-3.5 text-sm font-medium text-cream/80 hover:bg-cream/10 w-full min-h-[48px]">
          <LogOut size={20} /> Logout
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-cream flex">
      {/* Desktop / Tablet sidebar */}
      <aside className="hidden md:flex w-64 bg-choco flex-col fixed inset-y-0 left-0 z-40">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="w-72 bg-choco flex flex-col"><SidebarContent /></div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 md:ml-64 min-w-0">
        <header className="sticky top-0 z-30 bg-white border-b border-[#E5DFD3] px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="md:hidden text-choco" onClick={() => setOpen(true)} data-testid="admin-mobile-menu"><Menu size={24} /></button>
            <h1 className="font-display text-2xl font-bold text-choco">{title}</h1>
          </div>
        </header>
        <main className="p-5 sm:p-8">{children}</main>
      </div>
    </div>
  );
};
